/**
 * Spaces API Service
 *
 * Handles all API calls related to spaces for organizing saved queries.
 */

import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

function requireTenantPrefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return tenantApiPrefix(tid);
}

export interface Space {
  space_id: string;
  name: string;
  description: string | null;
  created_by_user_id: string;
  created_by_name: string;
  created_at: string;
  updated_at: string;
  query_count: number;
}

export interface CreateSpaceResponse {
  space_id: string;
  name: string;
  description: string | null;
  created_at: string;
  message: string;
}

export async function listSpaces(): Promise<Space[]> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/spaces`),
    { method: 'GET', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to list spaces' }));
    throw new Error(error.detail || 'Failed to list spaces');
  }
  const data = await response.json();
  return data.spaces;
}

export async function createSpace(name: string, description?: string): Promise<CreateSpaceResponse> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/spaces`),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ name, description: description || null }),
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create space' }));
    throw new Error(error.detail || 'Failed to create space');
  }
  return response.json();
}

export async function getSpaceQueries(spaceId: string, page: number = 1, limit: number = 20): Promise<any> {
  const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/spaces/${spaceId}/queries?${params}`),
    { method: 'GET', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to get space queries' }));
    throw new Error(error.detail || 'Failed to get space queries');
  }
  return response.json();
}

export async function updateSpace(spaceId: string, name?: string, description?: string): Promise<any> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/spaces/${spaceId}`),
    {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ...(name && { name }), ...(description !== undefined && { description }) }),
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update space' }));
    throw new Error(error.detail || 'Failed to update space');
  }
  return response.json();
}

export async function deleteSpace(spaceId: string): Promise<void> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/spaces/${spaceId}`),
    { method: 'DELETE', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete space' }));
    throw new Error(error.detail || 'Failed to delete space');
  }
}
